import {
  ConstrainMode,
  DetailsList,
  getTheme,
  IColumn,
  PrimaryButton,
  SelectionMode,
  Stack,
  Text,
  Toggle
} from "@fluentui/react";
import { Form, Formik } from "formik";
import { MouseEvent, useEffect, useRef, useState } from "react";
import * as Yup from "yup";

import AccordionContractDetailRow from "../components/AccordionContractDetailRow";
import { Contract, ContractItem } from "../contract.interface";
import { ActionColumnConfig } from "../ProductColumns/actionsColumn";
import { copyAndSortProducts } from "../ProductColumns/helpers";

import { EnergyItem } from "./EnergyItem";
import { InsertItem } from "./InsertItem/InsertItem";
import { InsertItemTag } from "./InsertItem/InsertItemTag";

import { buildColumns } from "@/components/Contracts/ProductColumns";
import { Card } from "@/components/Shared/Card";
import {
  CustomDrawer,
  DrawerActions,
  DrawerModules
} from "@/components/Shared/CustomDrawer";
import { Loader } from "@/components/Shared/Loader";
import { ContractsService } from "@/core/libs/api";
import { FieldsBuilder } from "@/core/libs/form-builder";
import { listStyles } from "@/core/libs/list-data/lib/styles";
import { MessageBar, messageBarTypes } from "@/core/libs/message-bar";
import { ContractOrigin } from "@/modules/Contracts/enums";
import { StyledStep } from "@/modules/Contracts/styles";
import { VerifyContractStepFunc } from "@/modules/Contracts/types";
import { ServicesCategoriesTypesEnum } from "@/modules/Settings/types/ServicesCategories.types";
import { toLocaleString } from "@/utils/ToLocaleString";

type ItemsStepProps = {
  nextStep: (contract, step?: number) => void;
  setContract: (contract) => void;
  verifyContractSteps: ({
    currentContract,
    updateCurrentStep
  }: VerifyContractStepFunc) => void;
  contract: Contract;
};

interface DrawerComponentProps {
  contract: Contract;
  close: (event?: unknown, refreshCondition?: boolean) => void;
  drawerContainerRef: React.RefObject<HTMLDivElement>;
  parcelLimit?: number;
}

const schema = Yup.object({
  shippingPrice: Yup.string().required("Obrigatório")
});

const serviceCategoriesWithLine = [
  ServicesCategoriesTypesEnum.BROADBAND,
  ServicesCategoriesTypesEnum.LINK_DEDICATED,
  ServicesCategoriesTypesEnum.MOBILE_LINE,
  ServicesCategoriesTypesEnum.MOBILE_LINE_PRE,
  ServicesCategoriesTypesEnum.PABX
];

const DefaultDrawerComponent: React.FC<DrawerComponentProps> = () => (
  <div>Tipo de serviço não suportado</div>
);

const drawerComponentMap: Record<string, React.FC<DrawerComponentProps>> = {
  // Serviços de linha
  [ServicesCategoriesTypesEnum.BROADBAND]: InsertItem,
  [ServicesCategoriesTypesEnum.LINK_DEDICATED]: InsertItem,
  [ServicesCategoriesTypesEnum.MOBILE_LINE]: InsertItem,
  [ServicesCategoriesTypesEnum.MOBILE_LINE_PRE]: InsertItem,
  [ServicesCategoriesTypesEnum.PABX]: InsertItem,
  // Tag
  [ServicesCategoriesTypesEnum.TAG]: InsertItemTag,
  // Energia
  [ServicesCategoriesTypesEnum.ENERGY]: EnergyItem
};

const getDrawerComponent = (type: ServicesCategoriesTypesEnum) => {
  return drawerComponentMap[type] || DefaultDrawerComponent;
};

const getLimitParcel = (parcels: Array<ContractItem>) => {
  for (let i = 0; i < parcels?.length; i++) {
    if (parcels[i].additionalInfo) return parcels[i].additionalInfo.parcel;
  }
};

const deleteItem = ({
  itemId,
  setMessage,
  refresh,
  contractId,
  setContract,
  verifyContractSteps,
  setProducts,
  closeDrawer
}) => {
  setMessage(undefined);

  const abortController = new AbortController();
  const { signal } = abortController;
  const contractsService = ContractsService();

  const params = {
    id: itemId
  };

  contractsService.deleteItem({ params, signal }).then(response => {
    if (response.error) {
      setMessage({
        message:
          "Não foi possível remover o benefício combo, por favor tente novamente",
        type: messageBarTypes.ERROR
      });
    } else {
      refresh({
        contractId,
        setMessage,
        setContract,
        verifyContractSteps,
        setProducts,
        closeDrawer
      });
    }
  });
};

const listShippings = async ({
  contractId,
  setShippingPrices,
  setInitialValues
}) => {
  const abortController = new AbortController();
  const { signal } = abortController;
  const service = ContractsService();

  const response = await service.listShippings({
    params: { id: contractId },
    signal
  });

  if (response?.data) {
    const newOption = response.data.map(option => {
      return {
        key: option.id,
        text: `${option.name} ${
          option.deliveryTime !== null && option.deliveryTime !== 0
            ? ` - ${option.deliveryTime} ${
                option.deliveryTime > 1 ? "Dias" : "Dia"
              } - ${
                Number(option.price) === 0
                  ? "Grátis"
                  : toLocaleString(option.price)
              }`
            : ""
        }`,
        id: `option-${option.id}`
      };
    });

    setShippingPrices(newOption);
    setInitialValues({
      shippingPrice: newOption[0].key
    });
  } else {
    setShippingPrices([]);
    setInitialValues({
      shippingPrice: ""
    });
  }
};

const refresh = ({
  contractId,
  setMessage,
  setContract,
  verifyContractSteps,
  setProducts,
  closeDrawer
}) => {
  const abortController = new AbortController();
  const { signal } = abortController;

  const service = ContractsService();
  service.get({ id: contractId.toString(), signal }).then(response => {
    if (response.error) {
      setMessage({
        message:
          "Não foi possível atualizar o contrato, por favor atualize a página e tente novamente",
        type: messageBarTypes.ERROR
      });
    } else {
      setContract(response.data);
      verifyContractSteps({ currentContract: response.data });
      setProducts(response.data.items || []);
    }

    closeDrawer();
  });
};

export function ItemsStep({
  contract,
  nextStep,
  setContract,
  verifyContractSteps
}: ItemsStepProps): JSX.Element {
  const isSimExempt = Boolean(contract.activationInstallment?.exemptSim);
  const drawerContainerRef = useRef<HTMLDivElement>(null);
  const refProducts = useRef(contract?.items);
  const refColumns = useRef<Array<IColumn>>(undefined);

  const [drawerState, setDrawerState] = useState({
    action: null,
    isOpen: false
  });
  const [message, setMessage] = useState(undefined);
  const [isCompactMode, setIsCompactMode] = useState(false);
  const [products, setProducts] = useState(contract?.items);
  const [columns, setColumns] = useState<IColumn[]>(undefined);
  const [shippingPrices, setShippingPrices] = useState([]);
  const [initialValues, setInitialValues] = useState({
    shippingPrice: contract?.shippingPrice || ""
  });

  const theme = getTheme();

  const updateProducts = updateProducts => {
    refProducts.current = updateProducts;
  };

  const updateAllProducts = newProducts => {
    updateProducts(newProducts);
    setProducts(newProducts);
  };

  const updateColumns = updateColumns => {
    refColumns.current = updateColumns;
    setColumns(updateColumns);
  };

  const onColumnClick = (
    event: MouseEvent<HTMLElement>,
    column: IColumn
  ): void => {
    let sortedItems = refProducts.current;
    let isSortedDescending = column.isSortedDescending;

    // If we've sorted this column, flip it.
    if (column.isSorted) {
      isSortedDescending = !isSortedDescending;
    }

    // Sort the items.
    sortedItems = copyAndSortProducts(
      sortedItems,
      column.fieldName,
      isSortedDescending
    );

    // Reset the items and columns to match the state.
    updateProducts(sortedItems);

    updateColumns(
      refColumns.current.map(col => {
        col.isSorted = col.key === column.key;

        if (col.isSorted) {
          col.isSortedDescending = isSortedDescending;
        } else {
          col.isSortedDescending = false;
        }

        return col;
      })
    );
  };

  const handleDeleteItem = (itemId: number) => {
    deleteItem({
      itemId,
      setMessage,
      refresh,
      contractId: contract.id,
      setContract,
      verifyContractSteps,
      setProducts: updateAllProducts,
      closeDrawer
    });
  };

  function openPanel(action: DrawerActions) {
    setDrawerState({ action: action, isOpen: true });
  }

  function closeDrawer(_?: unknown, refreshCondition?: boolean) {
    setDrawerState({ action: null, isOpen: false });

    if (refreshCondition) {
      refresh({
        contractId: contract.id,
        setMessage,
        setContract,
        verifyContractSteps,
        setProducts: updateAllProducts,
        closeDrawer
      });
    }
  }

  function cleanMessage() {
    setMessage(undefined);
  }

  function edit(values, { setSubmitting }) {
    setSubmitting(true);

    const abortController = new AbortController();
    const { signal } = abortController;
    const service = ContractsService();
    const params = {
      body: values,
      id: contract.id
    };

    if (contract.items.length === 0) {
      setMessage({
        message: "Por favor adicione um benefício combo ao contrato.",
        type: messageBarTypes.WARNING
      });

      setSubmitting(false);
    } else {
      service.edit({ params, signal }).then(response => {
        setSubmitting(false);

        if (response.error) {
          refresh({
            contractId: contract.id,
            setMessage,
            setContract,
            verifyContractSteps,
            setProducts: updateAllProducts,
            closeDrawer
          });

          setMessage({
            message:
              response.error.message ??
              "Não foi possível editar o contrato, por favor atualize a página e tente novamente.",
            type: messageBarTypes.ERROR
          });
        } else {
          setContract(response.data);
          nextStep(contract, 3);
        }
      });
    }
  }

  const actionConfig = (contract: Contract): ActionColumnConfig => ({
    menuItems: item => [
      {
        key: "removeItem",
        text: "Remover",
        onClick: () => handleDeleteItem(item.id),
        disabled: contract.origin !== ContractOrigin.LIBERTAS,
        title:
          contract.origin !== ContractOrigin.LIBERTAS
            ? "Não é possível remover itens de contratos de Autosserviço"
            : undefined
      }
    ]
  });

  const renderDrawerContent = () => {
    if (!drawerState.isOpen) return null;

    const type = contract.serviceCategory?.type;
    const DrawerComponent = getDrawerComponent(type);

    const extraProps = serviceCategoriesWithLine.includes(type)
      ? { parcelLimit: getLimitParcel(products) }
      : {};

    return (
      <DrawerComponent
        contract={contract}
        close={closeDrawer}
        drawerContainerRef={drawerContainerRef}
        {...extraProps}
      />
    );
  };

  useEffect(() => {
    const newColumns = buildColumns({
      isCompactMode,
      onColumnClick,
      serviceCategoryType: contract.serviceCategory.type,
      actionConfig: actionConfig(contract),
      isSimExempt
    });
    setColumns(newColumns);
    refColumns.current = newColumns;
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    listShippings({
      contractId: contract.id,
      setShippingPrices,
      setInitialValues
    });
    //eslint-disable-next-line
  }, [products]);

  useEffect(() => {
    if (columns) {
      const newColumns = buildColumns({
        isCompactMode,
        onColumnClick,
        serviceCategoryType: contract.serviceCategory.type,
        actionConfig: actionConfig(contract),
        isSimExempt
      });
      setColumns(newColumns);
      refColumns.current = newColumns;
    }
    //eslint-disable-next-line
  }, [isCompactMode, isSimExempt]);

  return (
    <Stack tokens={{ childrenGap: 20 }}>
      <Card>
        <StyledStep>
          {message && (
            <div style={{ marginBottom: 20 }}>
              <MessageBar message={message} dismissMessage={cleanMessage} />
            </div>
          )}

          <Stack horizontal horizontalAlign="space-between">
            <Stack.Item>
              <h2 className="ms-fontSize-20">Benefícios combos</h2>
            </Stack.Item>

            <Stack.Item>
              <PrimaryButton
                id="button-add-product"
                iconProps={{ iconName: "Add" }}
                onClick={() => openPanel(DrawerActions.INCLUDE)}
                disabled={Boolean(contract?.deletedAt)}
              />
            </Stack.Item>
          </Stack>

          <Toggle
            id="button-change-view"
            label="Exibir em modo compacto"
            checked={isCompactMode}
            onChange={(_, checked) => setIsCompactMode(checked)}
            onText="Compacto"
            offText="Normal"
          />

          <DetailsList
            selectionMode={SelectionMode.none}
            constrainMode={ConstrainMode.horizontalConstrained}
            items={[...refProducts.current]}
            columns={columns}
            styles={listStyles}
            onRenderRow={rowProps => (
              <AccordionContractDetailRow
                rowProps={rowProps}
                isCompactMode={isCompactMode}
                isSimExempt={isSimExempt}
                items={refProducts.current || []}
                refetchContractData={() => {
                  return;
                }}
              />
            )}
          />
          {refProducts.current.length < 1 && (
            <Stack
              horizontalAlign="center"
              verticalAlign="center"
              tokens={{ padding: "30px 0 42px 0" }}
              style={{
                borderBottom: `1px solid ${theme.palette.neutralLight}`
              }}
            >
              <Text variant="xLarge" style={{ textAlign: "center" }}>
                Adicione algum benefício combo a lista.
              </Text>
            </Stack>
          )}
        </StyledStep>
      </Card>
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={edit}
        enableReinitialize
      >
        {({ errors, touched, isSubmitting, setFieldValue, values }) => {
          return (
            <>
              {isSubmitting ? (
                <Loader />
              ) : (
                <Form>
                  <Stack
                    tokens={{ childrenGap: 15 }}
                    styles={{ root: { marginTop: 20 } }}
                  >
                    <Card>
                      <StyledStep>
                        <div className="ms-Grid" dir="ltr">
                          <div className="ms-Grid-row">
                            <div className="ms-Grid-col ms-sm12 ms-lg3">
                              <h2 className="ms-fontSize-20">Entrega</h2>
                            </div>
                            <div className="ms-Grid-col ms-sm12 ms-lg4">
                              <FieldsBuilder
                                {...{
                                  fields: [
                                    {
                                      id: "select-shipping-price",
                                      label: "Selecione um método de entrega",
                                      type: "select",
                                      name: "shippingPrice",
                                      value: values.shippingPrice,
                                      options: shippingPrices,
                                      required: true,
                                      disabled: shippingPrices?.length < 2
                                    }
                                  ],
                                  errors,
                                  touched,
                                  setFieldValue,
                                  values
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </StyledStep>
                    </Card>
                    <Stack.Item align="center">
                      <PrimaryButton
                        id="submit-button-step-3"
                        text={isSubmitting ? "Processando..." : "Avançar"}
                        type="submit"
                        disabled={isSubmitting || products?.length < 1}
                      />
                    </Stack.Item>
                  </Stack>
                </Form>
              )}
            </>
          );
        }}
      </Formik>
      <CustomDrawer
        isOpen={drawerState.isOpen}
        action={drawerState.action}
        module={DrawerModules.PRODUCT}
        onCloseDrawer={closeDrawer}
        size="large"
        drawerContainerRef={drawerContainerRef}
      >
        {renderDrawerContent()}
      </CustomDrawer>
    </Stack>
  );
}

export default ItemsStep;
